import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CreditCard, MessageCircle, ShieldCheck, Sparkles, Truck } from "lucide-react";
import { api } from "@/lib/api";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { formatARS } from "@/lib/format";
import { waLink, generalMsg, WHATSAPP_PRETTY } from "@/lib/whatsapp";

const HERO_IMG =
  "https://images.unsplash.com/photo-1603840148893-8c100f74d443?crop=entropy&cs=srgb&fm=jpg&q=85";

const METRICS = [
  { v: "24h", l: "Envío en Buenos Aires" },
  { v: "3", l: "Cuotas con Mercado Pago" },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} className="relative overflow-hidden pb-16 pt-32 lg:pb-24 lg:pt-44">
      <div className="pointer-events-none absolute -top-40 right-0 h-[560px] w-[560px] rounded-full bg-volt/10 blur-[140px]" />
      <div className="pointer-events-none absolute -left-52 top-1/2 h-[420px] w-[420px] rounded-full bg-volt/5 blur-[120px]" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 border border-volt/40 bg-volt/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-volt"
          >
            <Sparkles className="h-3.5 w-3.5" /> Buenos Aires — Envíos en 24h
          </motion.p>
          <h1 className="font-display text-[clamp(3.6rem,10.5vw,8.5rem)] font-black uppercase leading-[0.88] tracking-tighter text-white">
            {["DEL GYM", "A LA CALLE."].map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  className={`block ${i === 1 ? "text-volt" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.12 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
          >
            Ropa deportiva urbana Nike y Adidas para entrenar y para la calle: nuestro fuerte. Comprá online
            con Mercado Pago, pedí por WhatsApp y llevate <span className="font-semibold text-volt">10% OFF en tu segunda prenda</span>.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.62 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/tienda"
              data-testid="hero-explore-catalog-button"
              className="group inline-flex items-center gap-3 bg-volt px-8 py-4 font-display text-lg font-black uppercase tracking-wide text-black transition-colors hover:bg-volt-dim"
            >
              Explorar catálogo
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={waLink(generalMsg())}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-3 border border-white/20 px-8 py-4 font-display text-lg font-black uppercase tracking-wide text-white transition-colors hover:border-wa hover:text-wa"
            >
              <MessageCircle className="h-5 w-5" /> Pedir por WhatsApp
            </a>
          </motion.div>
          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-12 grid max-w-lg grid-cols-2 border border-white/10"
          >
            {METRICS.map((m, i) => (
              <div key={m.l} className={`px-4 py-4 ${i < 1 ? "border-r border-white/10" : ""}`}>
                <dt className="sr-only">{m.l}</dt>
                <dd className="font-mono text-2xl font-bold text-volt">{m.v}</dd>
                <dd className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{m.l}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="clip-frame relative overflow-hidden border border-white/10"
            >
              <img src={HERO_IMG} alt="Sportswear urbano MM Store" className="h-[420px] w-full object-cover lg:h-[560px]" />
              <div className="absolute inset-0 bg-volt/5 mix-blend-overlay" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="absolute -left-4 top-8 -rotate-6 bg-volt px-4 py-3 sm:-left-8"
            >
              <p className="font-display text-3xl font-black leading-none text-black">-10%</p>
              <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-black">2da prenda</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.05 }}
              className="absolute -bottom-5 right-4 flex items-center gap-3 border border-white/10 bg-panel px-4 py-3"
            >
              <CreditCard className="h-5 w-5 text-mp" />
              <div>
                <p className="font-mono text-xs font-bold text-white">3 cuotas</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">con Mercado Pago</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function DiscountBand({ products }) {
  const opts = products.slice(0, 14);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const pa = products.find((p) => p.id === a);
  const pb = products.find((p) => p.id === b);
  const ready = Boolean(pa && pb);
  const cheapest = ready ? Math.min(pa.price, pb.price) : 0;
  const saving = ready ? Math.round(cheapest * 0.1) : 0;
  const total = ready ? pa.price + pb.price - saving : 0;

  return (
    <section id="beneficio" className="border-y border-white/10 bg-panel">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-2 lg:py-24">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-widest text-volt">Beneficio exclusivo</p>
          <h2 className="mt-3 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-6xl">
            10% OFF en tu <span className="text-volt">2da prenda</span>
          </h2>
          <p className="mt-5 max-w-md text-zinc-400">
            Sumá dos o más productos a tu carrito y el descuento se aplica solo, sobre la prenda más económica. Sin cupones ni vueltas.
          </p>
          <ol className="mt-8 space-y-4">
            {["Elegí tus prendas", "Agregá 2 o más al carrito", "Pagá con Mercado Pago o coordiná por WhatsApp"].map((s, i) => (
              <li key={s} className="flex items-center gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-volt/40 font-mono text-xs font-bold text-volt">0{i + 1}</span>
                <span className="text-sm text-zinc-300">{s}</span>
              </li>
            ))}
          </ol>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="border border-volt/30 bg-obsidian p-6 sm:p-8" data-testid="discount-calculator">
            <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Simulá tu descuento</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">Prenda 1</label>
                <select
                  data-testid="discount-select-first-product"
                  value={a}
                  onChange={(e) => setA(e.target.value)}
                  className="mt-1.5 w-full border border-white/15 bg-panel-2 px-4 py-3 text-sm text-white outline-none focus:border-volt"
                >
                  <option value="">Elegí un producto</option>
                  {opts.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} — {formatARS(p.price)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">Prenda 2</label>
                <select
                  data-testid="discount-select-second-product"
                  value={b}
                  onChange={(e) => setB(e.target.value)}
                  className="mt-1.5 w-full border border-white/15 bg-panel-2 px-4 py-3 text-sm text-white outline-none focus:border-volt"
                >
                  <option value="">Elegí otro producto</option>
                  {opts.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} — {formatARS(p.price)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 border border-white/10 bg-panel p-5 text-center" data-testid="discount-calculator-result">
              {!ready ? (
                <p className="text-sm text-zinc-500">Elegí dos productos y mirá cuánto ahorrás</p>
              ) : (
                <>
                  <p className="font-mono text-sm text-zinc-400">Total con descuento</p>
                  <p className="mt-1 font-mono text-4xl font-bold text-white">{formatARS(total)}</p>
                  <p className="mt-2 font-mono text-xs font-bold uppercase tracking-widest text-volt">
                    Ahorrás {formatARS(saving)}
                  </p>
                </>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function InfoBand() {
  const items = [
    { icon: Truck, t: "Envíos rápidos", d: "CABA y GBA en 24–48h. Retiro coordinando por WhatsApp." },
    { icon: CreditCard, t: "Pagá como quieras", d: "Mercado Pago con tarjetas y cuotas, transferencia o efectivo coordinando por WhatsApp." },
    { icon: ShieldCheck, t: "Compra fácil", d: "Elegí, agregá al carrito y te contactamos por WhatsApp para confirmar todo." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {items.map((it, i) => (
          <Reveal key={it.t} delay={i * 0.08}>
            <div className="h-full border border-white/10 bg-panel p-7 transition-colors hover:border-volt/40">
              <it.icon className="h-7 w-7 text-volt" />
              <p className="mt-5 font-display text-xl font-black uppercase tracking-wide text-white">{it.t}</p>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">{it.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function WhatsAppCta() {
  return (
    <section className="border-y border-white/10 bg-panel">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-5 py-16 sm:px-8 lg:grid-cols-2 lg:py-24">
        <Reveal>
          <h2 className="font-display text-4xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-6xl">
            ¿Dudas? <br />
            <span className="text-volt">Chateá con nosotros</span>
          </h2>
          <p className="mt-5 max-w-md text-zinc-400">
            Talles, stock, combos y coordinación de envíos: te respondemos al toque. También podés pedir directamente por acá.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex flex-col items-start gap-6 bg-wa/5 p-8 sm:p-10" style={{ border: "1px solid rgba(37,211,102,0.3)" }}>
            <p className="font-display text-3xl font-black uppercase tracking-tight text-white">{WHATSAPP_PRETTY}</p>
            <a
              href={waLink(generalMsg())}
              target="_blank"
              rel="noreferrer"
              data-testid="home-whatsapp-cta-button"
              className="inline-flex items-center gap-3 bg-wa px-8 py-4 font-display text-lg font-black uppercase tracking-wide text-black transition-colors hover:bg-[#20bd5a]"
            >
              <MessageCircle className="h-5 w-5" /> Escribinos ahora
            </a>
            <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Lunes a sábados · Buenos Aires</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/productos").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  const featured = products
    .filter((p) => p.featured)
    .sort((a, b) => (a.category === "Ropa" ? 0 : 1) - (b.category === "Ropa" ? 0 : 1))
    .slice(0, 8);
  const bestSellers = products.filter((p) => p.category === "Ropa").slice(0, 8);

  return (
    <main>
      <Hero />
      <Marquee
        className="border-y border-black/20 bg-volt py-4 text-black"
        items={["MM Store Buenos Aires", "Nike Urban", "Adidas", "10% OFF 2da prenda", "Mercado Pago", "Del gym a la calle"]}
      />

      {featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 pb-16 sm:px-8 lg:pb-24">
          <Reveal className="flex items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest text-volt">Novedades</p>
              <h2 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">Lo más vendido</h2>
            </div>
            <Link to="/tienda" data-testid="home-view-all-button" className="group hidden items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-zinc-400 transition-colors hover:text-volt sm:inline-flex">
              Ver todo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      <DiscountBand products={products} />

      {bestSellers.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
          <Reveal className="flex items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest text-volt">Ropa deportiva · Lo que más vuela</p>
              <h2 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">Del gym a la calle</h2>
            </div>
            <Link to="/tienda?cat=Ropa" data-testid="home-view-ropa-button" className="group hidden items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-zinc-400 transition-colors hover:text-volt sm:inline-flex">
              Ver ropa <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {bestSellers.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      <InfoBand />
      <WhatsAppCta />
    </main>
  );
}
